import {
  Assessment,
  InvitedAssessmentManager,
} from "@appally/collections/Assessment";
import { ManagerAssessment } from "@appally/collections/ManagerAssessments";
import { User } from "@appally/collections/User";
import { collections } from "@appally/lib/data/firebase";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { doc, getDoc, getDocs, query, setDoc, where } from "firebase/firestore";
import {
  assessmentsWithAdditionalFields,
  getAssessment,
} from "../actions/assessment";
import { getCurrentUser, getUser } from "../actions/user";
import { assessmentApi } from "./assessmentApi";

export type InviteNewManagerInput = {
  assessmentId: string;
} & InvitedAssessmentManager;

export type RemoveManagerInput = {
  assessmentId: string;
  email: InvitedAssessmentManager["email"];
};

const managersToRecord = (managers: InvitedAssessmentManager[]) =>
  managers.reduce(
    (obj, manager, i) => {
      obj[i] = manager;
      return obj;
    },
    {} as Assessment["managers_invited"],
  );

// Define a service using a base URL and expected endpoints
export const managerApi = createApi({
  reducerPath: "managers",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Assessments", "AssessmentSettings"],
  endpoints: (builder) => ({
    // Get all the assessments that this user has access to
    getManagedAssessments: builder.query<
      (Assessment & { user?: User })[],
      void
    >({
      queryFn: async () => {
        // Get the current user
        const currentUser = await getCurrentUser();
        if (!currentUser) return { data: [] };

        // Get the assessments
        const assessments = await getDocs(
          query(
            collections.assessments,
            where("managed_by", "array-contains", currentUser.id),
          ),
        );
        if (assessments.empty) return { data: [] };

        const data = assessments.docs.map((doc) =>
          assessmentsWithAdditionalFields(doc.data()),
        );

        const withUsers = await Promise.all<Assessment & { user?: User }>(
          data.map(async (assessment) => {
            return {
              ...assessment,
              user: await getUser(assessment.assessment_user),
            };
          }),
        );

        return { data: withUsers };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: "Assessments" as const,
                id,
              })),
              { type: "Assessments", id: "LIST" },
            ]
          : [{ type: "Assessments", id: "LIST" }],
    }),

    // Get all the assessments that this user has access to
    getManagedAssessmentSettings: builder.query<
      ManagerAssessment | undefined,
      string
    >({
      queryFn: async (assessmentId) => {
        // Get the current user
        const currentUser = await getCurrentUser();
        if (!currentUser) return { data: undefined };

        // Get the manager records
        const thisDoc = await getDoc(
          doc(collections.assessment_managers, currentUser.id),
        );
        if (!thisDoc.exists()) return { data: undefined };
        const assessmentData = thisDoc.data().assessments;

        return {
          data: assessmentData ? assessmentData[assessmentId] : undefined,
        };
      },
    }),

    // Confirm the assessment helper to join my team
    inviteManager: builder.mutation<Assessment, InviteNewManagerInput>({
      queryFn: async (input) => {
        const { assessmentId, ...managerInput } = input;

        // Get the current user's assessment
        let assessment = await getAssessment(assessmentId);
        if (!assessment)
          throw new Error(
            "You don't have access to invite anyone to this assessment",
          );

        // Make sure we're not inviting ourselves
        const user = await getCurrentUser();
        if (input.email.toLowerCase() === user?.email.toLowerCase()) {
          throw new Error("You cannot invite yourself to this assessment");
        }

        // Get an array of the values
        const managers = Object.values(assessment.managers_invited ?? {});
        const index = managers.findIndex(
          (m) => m.email.toLowerCase() === input.email.toLowerCase(),
        );

        // Error if the manager already exists
        if (index !== -1) {
          throw new Error("This manager has already been invited");
        }

        // If the record doesn't already exists, add it
        managers.push({
          ...managerInput,
          added_at: Date.now(),
        });
        assessment.managers_invited = managersToRecord(managers);

        // Update the assessment
        await setDoc(
          doc(collections.assessments, assessment.id),
          {
            managers_invited: assessment.managers_invited,
          },
          { merge: true },
        );

        return { data: assessment };
      },
      onQueryStarted: async (input, { dispatch, queryFulfilled }) => {
        const { assessmentId, ...managerInput } = input;
        const patch = dispatch(
          assessmentApi.util.updateQueryData(
            "getAssessment",
            assessmentId,
            (draft) => {
              const managers = Object.values(draft.managers_invited ?? {});
              managers.push({
                ...managerInput,
                added_at: input.added_at,
              });
              draft.managers_invited = managersToRecord(managers);
            },
          ),
        );

        try {
          await queryFulfilled;
          dispatch(
            assessmentApi.util.invalidateTags([
              { type: "Assessments", id: assessmentId },
            ]),
          );
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (result) =>
        result ? [{ type: "Assessments", id: result.id }] : [],
    }),

    // Confirm the assessment helper to join my team
    removeManager: builder.mutation<Assessment, RemoveManagerInput>({
      queryFn: async (input) => {
        // Get the current user's assessment
        let assessment = await getAssessment(input.assessmentId);
        if (!assessment)
          throw new Error(
            "You don't have access to remove anyone from this assessment",
          );

        // Get an array of the values
        const managers = Object.values(
          assessment.managers_invited ?? {},
        ).filter((m) => {
          return m.email.toLowerCase() !== input.email.toLowerCase();
        });

        // Update the value
        const managers_invited = managersToRecord(managers);

        // Update the assessment
        await setDoc(
          doc(collections.assessments, assessment.id),
          {
            managers_invited,
          },
          { mergeFields: ["managers_invited"] },
        );

        return { data: { ...assessment, managers_invited } };
      },
      onQueryStarted: async (input, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          assessmentApi.util.updateQueryData(
            "getAssessment",
            input.assessmentId,
            (draft) => {
              const managers = Object.values(
                draft.managers_invited ?? {},
              ).filter(
                (manager) =>
                  manager.email.toLowerCase() !== input.email.toLowerCase(),
              );
              draft.managers_invited = managersToRecord(managers);
            },
          ),
        );

        try {
          await queryFulfilled;
          dispatch(
            assessmentApi.util.invalidateTags([
              { type: "Assessments", id: input.assessmentId },
            ]),
          );
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (result) =>
        result ? [{ type: "Assessments", id: result.id }] : [],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetManagedAssessmentsQuery,
  useGetManagedAssessmentSettingsQuery,
  useInviteManagerMutation,
  useRemoveManagerMutation,
} = managerApi;
